import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import customerService, { type Customer } from "@/services/customer";
import productService, { type Product } from "@/services/product";
import quotationService, {
  type QuotationData,
  type QuotationItem,
  type QuotationRecord,
} from "@/services/quotation";
import settingService from "@/services/setting";
import warehouseService, { type Warehouse } from "@/services/warehouse";
import { useCurrentUser } from "@/composables/useCurrentUser";
import stateStore from "@/utils/store";

type QuotationFormState = {
  date: string;
  reference_no: string;
  customer_id: number | null;
  warehouse_id: number | null;
  status: string;
  note: string;
  tax: number;
  tax_type: "fixed" | "percentage";
  discount: number;
  discount_type: "fixed" | "percentage";
  shipping: number;
  items: QuotationItem[];
};

type ProductOption = Product & {
  stock: number;
  canSelect: boolean;
};

const normalizeSearch = (value: string) => value.replace(/\s+/g, " ").trim();

const createInitialForm = (): QuotationFormState => ({
  date: new Date().toISOString().slice(0, 10),
  reference_no: "",
  customer_id: null,
  warehouse_id: null,
  status: "pending",
  note: "",
  tax: 0,
  tax_type: "fixed",
  discount: 0,
  discount_type: "fixed",
  shipping: 0,
  items: [],
});

const getProductStock = (product: Product, warehouseId: number | null) => {
  if (!warehouseId) {
    return Number(product.qty ?? 0);
  }

  return (product.stocks || [])
    .filter((stock) => Number(stock.warehouse_id) === Number(warehouseId))
    .reduce((total, stock) => total + Number(stock.qty ?? stock.quantity ?? 0), 0);
};

const toQuotationItem = (product: Product): QuotationItem => ({
  product_id: Number(product.id),
  product_code: product.code,
  product_name: product.name,
  unit_price: Number(product.price ?? 0),
  quantity: 1,
  subtotal: Number(product.price ?? 0),
});

export function useQuotationList() {
  const router = useRouter();
  const { t } = useI18n();
  const searchQuery = ref("");
  const loading = ref(false);
  const detailsLoading = ref(false);
  const quotations = ref<QuotationRecord[]>([]);
  const selectedQuotation = ref<QuotationRecord | null>(null);
  const showConvertedModal = ref(false);
  const warehouses = ref<Warehouse[]>([]);
  const filters = reactive({
    warehouse_id: "",
    status: "",
  });
  const activeFilters = ref({
    warehouse_id: "",
    status: "",
  });

  const fetchWarehouses = async () => {
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchQuotationsList = async () => {
    loading.value = true;
    try {
      const response = await quotationService.getQuotations({
        search: searchQuery.value,
        ...activeFilters.value,
      });

      quotations.value = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
    } catch (error) {
      console.error("Error fetching quotations:", error);
      await Swal.fire({
        icon: "error",
        title: t("quotation_page.failed_to_load"),
        text: t("quotation_page.unable_to_load_quotations"),
      });
    } finally {
      loading.value = false;
    }
  };

  const applyFilters = () => {
    activeFilters.value = { ...filters };
    fetchQuotationsList();
  };

  const resetFilters = () => {
    filters.warehouse_id = "";
    filters.status = "";
    applyFilters();
  };

  const handleViewDetails = async (quotation: QuotationRecord) => {
    if (!quotation?.id) {
      selectedQuotation.value = quotation;
      return;
    }

    detailsLoading.value = true;
    try {
      selectedQuotation.value = await quotationService.getQuotation(quotation.id);
    } catch (error) {
      console.error("Error loading quotation details:", error);
      selectedQuotation.value = quotation;
    } finally {
      detailsLoading.value = false;
    }
  };

  const handleDeleteQuotation = async (quotation: QuotationRecord) => {
    if (!quotation.id) return;

    const result = await Swal.fire({
      title: t("quotation_page.delete_quotation_title"),
      text: t("quotation_page.delete_quotation_text", { ref: quotation.reference_no }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("quotation_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      await quotationService.deleteQuotation(quotation.id);
      await fetchQuotationsList();
      await Swal.fire({
        icon: "success",
        title: t("quotation_page.deleted"),
        text: t("quotation_page.quotation_deleted_successfully"),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error deleting quotation:", error);
      await Swal.fire({
        icon: "error",
        title: t("quotation_page.delete_failed"),
        text: t("quotation_page.unable_to_delete_quotation"),
      });
    }
  };

  const handleCreateSale = async (quotation: QuotationRecord) => {
    if (quotation.status === "completed") {
      showConvertedModal.value = true;
      return;
    }

    if (!quotation.id) return;

    try {
      const fullQuotation = await quotationService.getQuotation(quotation.id);

      stateStore.selectedCustomerId = fullQuotation.customer_id || null;
      stateStore.selectedWarehouseId = fullQuotation.warehouse_id || null;

      const products = (fullQuotation.items || []).map((item) => ({
        id: item.product_id,
        name: item.product_name || "",
        code: item.product_code || "",
        qty: Number(item.quantity || 0),
        stock: 0,
        price: Number(item.unit_price || item.price || 0),
        discount: Number(item.discount || 0),
        discount_type: "fixed" as const,
        subTotal: Number(item.subtotal || 0),
      }));

      stateStore.selectedProducts = products;
      stateStore.orderDiscount = Number(fullQuotation.discount || 0);
      stateStore.orderDiscountType = "fixed";
      stateStore.shippingCost = Number(fullQuotation.shipping || 0);
      stateStore.orderTax = Number(fullQuotation.tax || 0);
      stateStore.orderTaxType = "fixed";

      localStorage.setItem("pos_selected_products", JSON.stringify(products));
      localStorage.setItem("pos_customer_id", String(fullQuotation.customer_id || ""));
      localStorage.setItem("pos_warehouse_id", String(fullQuotation.warehouse_id || ""));
      localStorage.setItem("pos_order_tax", String(fullQuotation.tax || 0));
      localStorage.setItem("pos_order_discount", String(fullQuotation.discount || 0));
      localStorage.setItem("pos_shipping", String(fullQuotation.shipping || 0));

      router.push({ path: "/create-sales", query: { quotation_id: quotation.id } });
    } catch (error) {
      console.error("Error converting quotation:", error);
      await Swal.fire({
        icon: "error",
        title: t("quotation_page.convert_failed"),
        text: t("quotation_page.failed_to_convert_quotation_to_sale"),
      });
    }
  };

  watch(searchQuery, fetchQuotationsList);

  onMounted(async () => {
    await fetchWarehouses();
    await fetchQuotationsList();
  });

  return {
    searchQuery,
    loading,
    detailsLoading,
    quotations,
    selectedQuotation,
    showConvertedModal,
    warehouses,
    filters,
    applyFilters,
    resetFilters,
    handleViewDetails,
    handleDeleteQuotation,
    handleCreateSale,
  };
}

function buildQuotationForm(mode: "create" | "edit") {
  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();
  const { fetchCurrentUser } = useCurrentUser();

  const form = reactive<QuotationFormState>(createInitialForm());
  const customers = ref<Customer[]>([]);
  const warehouses = ref<Warehouse[]>([]);
  const searchQuery = ref("");
  const searchResults = ref<ProductOption[]>([]);
  const showResults = ref(false);
  const isSubmitting = ref(false);
  const isLoadingData = ref(false);
  const quotationId = computed(() => Number(route.params.id || 0));
  const profileWarehouseId = ref<number | null>(null);
  const warehouseLockedToProfile = computed(() => profileWarehouseId.value !== null);
  const breadcrumbItems =
    mode === "edit"
      ? [
          { label: "quotation_list", to: "/quotation-list" },
          { label: "edit_quotation" },
        ]
      : [
          { label: "quotation_list", to: "/quotation-list" },
          { label: "create_quotation" },
        ];

  const totalItems = computed(() =>
    form.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
  );

  const totalCost = computed(() =>
    form.items.reduce((sum, item) => sum + Number(item.unit_price || 0) * Number(item.quantity || 0), 0)
  );

  const calculatedDiscount = computed(() => {
    if (form.discount_type === "percentage") {
      return (totalCost.value * Number(form.discount || 0)) / 100;
    }

    return Number(form.discount || 0);
  });

  const calculatedTax = computed(() => {
    const amountAfterDiscount = Math.max(0, totalCost.value - calculatedDiscount.value);
    if (form.tax_type === "percentage") {
      return (amountAfterDiscount * Number(form.tax || 0)) / 100;
    }

    return Number(form.tax || 0);
  });

  const grandTotal = computed(
    () => Math.max(0, totalCost.value - calculatedDiscount.value) + calculatedTax.value + Number(form.shipping || 0)
  );

  const fetchBaseData = async () => {
    const [customerData, warehouseData, settings, currentUser] = await Promise.all([
      customerService.getCustomers(),
      warehouseService.getWarehouses(),
      settingService.getSettings(),
      fetchCurrentUser(true),
    ]);

    customers.value = customerData;
    warehouses.value = warehouseData;

    if (currentUser?.warehouse_id) {
      profileWarehouseId.value = Number(currentUser.warehouse_id);
      form.warehouse_id = Number(currentUser.warehouse_id);
    } else if (settings?.default_warehouse) {
      form.warehouse_id = Number(settings.default_warehouse);
    } else if (warehouses.value.length) {
      form.warehouse_id = Number(warehouses.value[0].id ?? null);
    }

    if (settings?.default_customer) {
      form.customer_id = Number(settings.default_customer);
    } else if (customers.value.length) {
      form.customer_id = Number(customers.value[0].id ?? null);
    }
  };

  const fetchCreateDefaults = async () => {
    form.reference_no = await quotationService.getNextReference();
  };

  const fetchQuotationDetails = async () => {
    if (!quotationId.value) return;

    const quotation = await quotationService.getQuotation(quotationId.value);
    form.reference_no = quotation.reference_no || "";
    form.date = quotation.created_at ? quotation.created_at.slice(0, 10) : form.date;
    form.customer_id = quotation.customer_id ?? null;
    form.warehouse_id = quotation.warehouse_id ?? null;
    form.status = quotation.status || "pending";
    form.note = quotation.note || "";
    form.tax = Number(quotation.tax || 0);
    form.tax_type = "fixed";
    form.discount = Number(quotation.discount || 0);
    form.discount_type = "fixed";
    form.shipping = Number(quotation.shipping || 0);
    form.items = (quotation.items || []).map((item) => ({
      ...item,
      unit_price: Number(item.unit_price || item.price || 0),
      quantity: Number(item.quantity || 1),
      subtotal: Number(item.subtotal || Number(item.unit_price || item.price || 0) * Number(item.quantity || 1)),
    }));
  };

  const handleSearch = async () => {
    const normalizedQuery = normalizeSearch(searchQuery.value);
    if (!normalizedQuery && searchQuery.value !== " ") {
      searchResults.value = [];
      showResults.value = false;
      return;
    }

    try {
      const products = await productService.getProducts({
        search: normalizedQuery || undefined,
        warehouse_id: form.warehouse_id || undefined,
      });

      searchResults.value = products.map((product) => ({
        ...product,
        stock: getProductStock(product, form.warehouse_id),
        canSelect: Number(product.type ?? 1) !== 1 || getProductStock(product, form.warehouse_id) > 0,
      }));
      showResults.value = true;
    } catch (error) {
      console.error("Error searching quotation products:", error);
      searchResults.value = [];
      showResults.value = false;
    }
  };

  const addProduct = (product: ProductOption) => {
    if (Number(product.type ?? 1) === 1 && Number(product.stock ?? 0) <= 0) {
      return;
    }

    const existing = form.items.find((item) => Number(item.product_id) === Number(product.id));

    if (existing) {
      existing.quantity = Number(existing.quantity || 0) + 1;
      existing.subtotal = Number(existing.unit_price || 0) * Number(existing.quantity || 0);
    } else {
      form.items.push(toQuotationItem(product));
    }

    searchQuery.value = "";
    searchResults.value = [];
    showResults.value = false;
  };

  const updateQty = (index: number, delta: number) => {
    const item = form.items[index];
    if (!item) return;

    item.quantity = Math.max(1, Number(item.quantity || 0) + delta);
    item.subtotal = Number(item.unit_price || 0) * Number(item.quantity || 0);
  };

  const removeItem = (index: number) => {
    form.items.splice(index, 1);
  };

  const updateItemQuantity = (index: number, value: number) => {
    const item = form.items[index];
    if (!item) return;

    item.quantity = Math.max(1, Number(value || 1));
    item.subtotal = Number(item.unit_price || 0) * Number(item.quantity || 0);
  };

  const submitQuotation = async () => {
    if (!form.reference_no.trim() || !form.customer_id || !form.warehouse_id || !form.items.length) {
      await Swal.fire({
        icon: "warning",
        title: t("quotation_page.incomplete_quotation"),
        text: t("quotation_page.required_fields_and_product"),
      });
      return;
    }

    isSubmitting.value = true;
    try {
      const customer = customers.value.find((item) => item.id === form.customer_id);
      const warehouse = warehouses.value.find((item) => item.id === form.warehouse_id);

      const data: QuotationData = {
        reference_no: form.reference_no.trim(),
        customer_id: form.customer_id,
        customer_name: customer?.name,
        warehouse_id: form.warehouse_id,
        warehouse_name: warehouse?.name,
        note: form.note,
        tax: calculatedTax.value,
        discount: calculatedDiscount.value,
        shipping: Number(form.shipping || 0),
        total: totalCost.value,
        grand_total: grandTotal.value,
        items: form.items.map((item) => ({
          ...item,
          subtotal: Number(item.unit_price || 0) * Number(item.quantity || 0),
        })),
        status: form.status || "pending",
      };

      if (mode === "edit" && quotationId.value) {
        await quotationService.updateQuotation(quotationId.value, data);
        await Swal.fire(t("quotation_page.success"), t("quotation_page.quotation_updated_successfully"), "success");
      } else {
        await quotationService.createQuotation(data);
        await Swal.fire(t("quotation_page.success"), t("quotation_page.quotation_created_successfully"), "success");
      }

      router.push("/quotation-list");
    } catch (error: any) {
      console.error("Error saving quotation:", error);
      await Swal.fire(
        t("quotation_page.error"),
        error?.response?.data?.message || t("quotation_page.failed_to_save_quotation"),
        "error"
      );
    } finally {
      isSubmitting.value = false;
    }
  };

  onMounted(async () => {
    isLoadingData.value = true;
    try {
      await fetchBaseData();
      if (mode === "edit") {
        await fetchQuotationDetails();
      } else {
        await fetchCreateDefaults();
      }
    } finally {
      isLoadingData.value = false;
    }
  });

  return {
    form,
    customers,
    warehouses,
    searchQuery,
    searchResults,
    showResults,
    isSubmitting,
    isLoadingData,
    totalItems,
    totalCost,
    calculatedDiscount,
    calculatedTax,
    grandTotal,
    breadcrumbItems,
    warehouseLockedToProfile,
    handleSearch,
    addProduct,
    updateQty,
    removeItem,
    updateItemQuantity,
    submitQuotation,
  };
}

export const useCreateQuotationForm = () => buildQuotationForm("create");
export const useEditQuotationForm = () => buildQuotationForm("edit");
